import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { QUERY_KEYS } from "@/hooks/use-queries";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import type { HospitalPartner } from "@shared/schema";
import { CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { StatusBadge } from "./shared";

export function StatusChangeDialog({
  partner,
  open,
  onOpenChange,
  onSuccess
}: {
  partner: HospitalPartner;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [newStatus, setNewStatus] = useState<string>(partner.status);
  const [notes, setNotes] = useState("");
  
  useEffect(() => {
    if (open) {
      setNewStatus(partner.status);
      setNotes("");
    }
  }, [open, partner.status]);
  
  const updateMutation = useMutation({
    mutationFn: (data: { status: string; notes?: string }) => 
      apiRequest("PATCH", `/api/hospital-partners/${partner.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hospitalPartners });
      queryClient.invalidateQueries({ queryKey: ["/api/hospital-partners", partner.id, "dashboard"] });
      toast({ 
        title: t('hospitalPartners.statusChanged', 'Status Changed'), 
        description: t('hospitalPartners.statusChangedDesc', 'Partner status has been updated successfully.') 
      });
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: t('common.error', 'Error'), description: error.message, variant: "destructive" });
    },
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedNotes = notes 
      ? `${partner.notes || ''}\n[${format(new Date(), 'yyyy-MM-dd HH:mm')}] Status changed to ${newStatus}: ${notes}`.trim()
      : partner.notes || undefined;
    updateMutation.mutate({ status: newStatus, notes: updatedNotes });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('hospitalPartners.changeStatus', 'Change Partner Status')}</DialogTitle>
          <DialogDescription>
            {t('hospitalPartners.changeStatusDesc', 'Update the status for')} {partner.partnerName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('hospitalPartners.currentStatus', 'Current Status')}</Label>
            <div><StatusBadge status={partner.status} /></div>
          </div>
          
          <div className="space-y-2">
            <Label>{t('hospitalPartners.newStatus', 'New Status')}</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="select-new-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  <div className="flex items-center"><Clock className="h-4 w-4 mr-2" /> {t('hospitalPartners.statusPending', 'Pending')}</div>
                </SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-green-500" /> {t('hospitalPartners.statusActive', 'Active')}</div>
                </SelectItem>
                <SelectItem value="suspended">
                  <div className="flex items-center"><XCircle className="h-4 w-4 mr-2 text-red-500" /> {t('hospitalPartners.statusSuspended', 'Suspended')}</div>
                </SelectItem>
                <SelectItem value="inactive">
                  <div className="flex items-center"><AlertCircle className="h-4 w-4 mr-2" /> {t('hospitalPartners.statusInactive', 'Inactive')}</div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>{t('hospitalPartners.statusChangeReason', 'Reason for Change (Optional)')}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('hospitalPartners.statusChangeReasonPlaceholder', 'Enter reason for status change...')}
              rows={3}
              data-testid="input-status-reason"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending || newStatus === partner.status} data-testid="button-change-status">
              {updateMutation.isPending ? t('common.saving', 'Saving...') : t('hospitalPartners.changeStatus', 'Change Status')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
